import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Trash2, Save, ShoppingCart, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useCreateOrder, useSuppliers, useCreateSupplier } from '../hooks/usePurchases';
import { useItems, useCreateItem } from '../../inventory/hooks/useInventory';
import { useQueryClient } from '@tanstack/react-query';
import { ReceiptScanner, type ParsedReceipt } from '../components/ReceiptScanner';

const UNITS = [
  'g', 'kg', 'ml', 'litros', 'unidades', 'paquetes', 'pacas', 'cajas',
  'bultos', 'sixpack', 'botellas', 'bolsas', 'latas', 'galones', 'porciones', 'libras'
];

interface ItemRow {
  inventory_item_id: string | number;
  quantity: number;
  unit: string;
  unit_price: number;
}

const NewPurchasePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: suppliers } = useSuppliers();
  const { data: inventoryPage } = useItems({ limit: 200 });
  const createOrderMut = useCreateOrder();
  const queryClient = useQueryClient();

  const inventoryItems = inventoryPage?.data ?? [];

  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [purchasedAt, setPurchasedAt] = useState(new Date().toISOString().slice(0, 10));
  const [tax, setTax] = useState(0);
  const [notes, setNotes] = useState('');
  const [applyStock, setApplyStock] = useState(true);
  const [expenseCategory, setExpenseCategory] = useState('INSUMOS');
  const [showScanner, setShowScanner] = useState(false);
  const [items, setItems] = useState<ItemRow[]>([
    { inventory_item_id: '', quantity: 1, unit: 'unidades', unit_price: 0 },
  ]);

  useEffect(() => {
    if (location.state?.prefillItems) {
      setItems(location.state.prefillItems);
    }
  }, [location.state]);

  // Quick add states
  const addSupplier = useCreateSupplier();
  const addItemMut = useCreateItem();
  const [showQuickSupplier, setShowQuickSupplier] = useState(false);
  const [quickSupplierName, setQuickSupplierName] = useState('');
  const [showQuickItem, setShowQuickItem] = useState<{ active: boolean; rowIndex: number }>({ active: false, rowIndex: -1 });
  const [quickItemName, setQuickItemName] = useState('');

  const subtotal = items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);
  const total = subtotal + tax;

  const fmt = (n: number) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

  const addItem = () => setItems(prev => [...prev, { inventory_item_id: '', quantity: 1, unit: 'unidades', unit_price: 0 }]);

  const removeItem = (i: number) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const changeItem = (i: number, field: keyof ItemRow, value: string | number) =>
    setItems(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: value } : row));

  const handleSelectInventoryItem = (i: number, itemIdStr: string) => {
    const selectedItem = inventoryItems.find(item => String(item.id) === itemIdStr);
    const unit = selectedItem?.purchase_unit || selectedItem?.unit || 'unidades';
    const suggestedPrice = (selectedItem?.purchase_price !== null && selectedItem?.purchase_price !== undefined && Number(selectedItem.purchase_price) > 0)
      ? Number(selectedItem.purchase_price)
      : (selectedItem?.cost ? Number(((selectedItem.cost || 0) * (selectedItem.conversion_factor || 1)).toFixed(2)) : 0);

    setItems(prev => prev.map((row, idx) => idx === i ? {
      ...row,
      inventory_item_id: itemIdStr,
      unit,
      unit_price: row.unit_price > 0 ? row.unit_price : suggestedPrice
    } : row));
  };


  const [creatingItems, setCreatingItems] = useState(false);

  const handleParsedReceipt = async (parsed: ParsedReceipt) => {
    setShowScanner(false);

    // Pre-fill basic fields
    if (parsed.purchased_at) setPurchasedAt(parsed.purchased_at);
    if (parsed.invoice_number) setInvoiceNumber(parsed.invoice_number);
    if (parsed.tax > 0) setTax(parsed.tax);
    if (parsed.notes) setNotes(parsed.notes);
    if (parsed.expense_category) setExpenseCategory(parsed.expense_category);

    // Try to auto-match supplier by name
    if (parsed.supplier_name && suppliers) {
      const match = suppliers.find(s =>
        s.name.toLowerCase().includes(parsed.supplier_name!.toLowerCase()) ||
        parsed.supplier_name!.toLowerCase().includes(s.name.toLowerCase())
      );
      if (match) setSupplierId(match.id);
    }

    if (parsed.items.length === 0) return;

    setCreatingItems(true);
    let created = 0;
    let matched = 0;

    // Build rows: match existing items, auto-create the ones that don't exist
    const newRows: ItemRow[] = await Promise.all(
      parsed.items.map(async (pi) => {
        const unit = pi.unit || 'unidades';

        // Try to find existing inventory item
        const existingItem = inventoryItems.find(inv =>
          inv.name.toLowerCase().includes(pi.name.toLowerCase()) ||
          pi.name.toLowerCase().includes(inv.name.toLowerCase())
        );

        if (existingItem) {
          matched++;
          return {
            inventory_item_id: existingItem.id,
            quantity: pi.quantity,
            unit,
            unit_price: pi.unit_price,
          };
        }

        // Auto-create the item in inventory
        try {
          const newItem = await addItemMut.mutateAsync({
            name: pi.name,
            unit,
          });
          created++;
          return {
            inventory_item_id: newItem.id,
            quantity: pi.quantity,
            unit,
            unit_price: pi.unit_price,
          };
        } catch {
          // If creation fails for some reason, leave blank for manual selection
          return {
            inventory_item_id: '' as '',
            quantity: pi.quantity,
            unit,
            unit_price: pi.unit_price,
          };
        }
      })
    );

    // Refresh inventory list so new items appear in selects
    if (created > 0) {
      await queryClient.refetchQueries({ queryKey: ['inventory', 'items'] });
    }

    setItems(newRows);
    setCreatingItems(false);

    const parts: string[] = [`${parsed.items.length} ítem${parsed.items.length !== 1 ? 's' : ''} detectados`];
    if (matched > 0) parts.push(`${matched} ya existían en inventario`);
    if (created > 0) parts.push(`${created} creados automáticamente`);

    toast.success(`✓ Recibo analizado: ${parts.join(', ')}. Revisa y corrige si es necesario.`, {
      autoClose: 6000,
    });
  };

  const handleSubmit = async () => {
    if (!supplierId) { toast.error('Selecciona un proveedor'); return; }
    if (!purchasedAt) { toast.error('Indica la fecha de compra'); return; }
    const validItems = items.filter(i => i.inventory_item_id !== '' && i.quantity > 0 && i.unit_price >= 0);
    if (validItems.length === 0) { toast.error('Agrega al menos un producto válido'); return; }

    try {
      await createOrderMut.mutateAsync({
        supplier_id: supplierId as number,
        invoice_number: invoiceNumber || undefined,
        purchased_at: purchasedAt,
        tax,
        notes: notes || undefined,
        apply_stock: applyStock,
        items: validItems.map(i => ({
          inventory_item_id: Number(i.inventory_item_id),
          quantity: i.quantity,
          unit: i.unit,
          unit_price: i.unit_price,
        })),
      });
      toast.success('Factura registrada correctamente');
      navigate('/purchases');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Error al registrar la factura');
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-4xl space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted">Compras</p>
            <h1 className="text-2xl font-bold text-[color:var(--color-text)] flex items-center gap-2">
              <ShoppingCart size={24} className="text-primary" /> Registrar Factura de Compra
            </h1>
          </div>
          <button
            type="button"
            onClick={() => setShowScanner(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-violet-700 transition"
          >
            <Sparkles size={16} /> Escanear recibo con IA
          </button>
        </div>

        {showScanner && (
          <ReceiptScanner onParsed={handleParsedReceipt} onClose={() => setShowScanner(false)} />
        )}

        {creatingItems && (
          <div className="flex items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-700">
            <Loader2 size={16} className="animate-spin shrink-0" />
            <span>Creando insumos nuevos en el inventario… un momento.</span>
          </div>
        )}

        {/* General info */}
        <div className="rounded-2xl border border-divider bg-surface p-6 space-y-4 shadow-sm">
          <h2 className="text-sm font-bold text-[color:var(--color-text)]">Información general</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="supplier">Proveedor *</label>
              <div className="flex items-center gap-2">
                <select
                  id="supplier"
                  value={supplierId}
                  onChange={e => setSupplierId(Number(e.target.value) || '')}
                  className="flex-1 rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">-- Selecciona proveedor --</option>
                  {(suppliers ?? []).map(s => (
                    <option key={s.id} value={s.id}>{s.name}{s.city ? ` (${s.city})` : ''}</option>
                  ))}
                </select>
                <button
                  onClick={() => setShowQuickSupplier(true)}
                  className="h-[42px] px-3 rounded-xl border border-primary/30 text-primary hover:bg-primary/5 transition"
                  title="Nuevo proveedor"
                >
                  <Plus size={18} />
                </button>
              </div>

              {showQuickSupplier && (
                <div className="mt-2 p-3 rounded-xl border border-primary/20 bg-primary/5 space-y-2">
                  <input
                    type="text" autoFocus
                    placeholder="Nombre del nuevo proveedor..."
                    value={quickSupplierName}
                    onChange={e => setQuickSupplierName(e.target.value)}
                    className="w-full rounded-lg border border-divider px-3 py-2 text-sm focus:outline-none"
                  />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowQuickSupplier(false)} className="text-xs text-muted">Cancelar</button>
                    <button
                      onClick={async () => {
                        if (!quickSupplierName) return;
                        try {
                          const res = await addSupplier.mutateAsync({ name: quickSupplierName });
                          // Forzar recarga inmediata de la lista de proveedores
                          await queryClient.refetchQueries({ queryKey: ['purchases', 'suppliers'] });

                          setSupplierId(res.id);
                          setShowQuickSupplier(false);
                          setQuickSupplierName('');
                          toast.success('Proveedor agregado y seleccionado');
                        } catch (e) {
                          toast.error('Error al crear proveedor');
                        }
                      }}
                      className="text-xs font-bold text-primary"
                    >
                      Crear
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="invoice">Número de factura</label>
              <input
                id="invoice"
                type="text"
                value={invoiceNumber}
                onChange={e => setInvoiceNumber(e.target.value)}
                placeholder="Ej: FAC-2024-001"
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="date">Fecha de compra *</label>
              <input
                id="date"
                type="date"
                value={purchasedAt}
                onChange={e => setPurchasedAt(e.target.value)}
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="tax">IVA / Otros impuestos ($)</label>
              <input
                id="tax"
                type="number"
                min={0}
                value={tax}
                onChange={e => setTax(parseFloat(e.target.value) || 0)}
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="category">Categoría de gasto</label>
              <select
                id="category"
                value={expenseCategory}
                onChange={e => setExpenseCategory(e.target.value)}
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="INSUMOS">🥩 Insumos / ingredientes</option>
                <option value="SERVICIOS">🔧 Servicios externos</option>
                <option value="NOMINA">👥 Nómina / personal</option>
                <option value="MANTENIMIENTO">⚙️ Mantenimiento / equipos</option>
                <option value="ARRIENDO">🏠 Arriendo</option>
                <option value="SERVICIOS_PUBLICOS">💡 Servicios públicos</option>
                <option value="OTROS">📦 Otros</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="notes">Notas</label>
              <textarea
                id="notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                placeholder="Observaciones adicionales..."
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={applyStock}
              onChange={e => setApplyStock(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            <span className="text-sm font-medium text-[color:var(--color-text)]">
              Aplicar automáticamente al inventario
            </span>
            <span className="text-xs text-muted">(crea movimiento de entrada)</span>
          </label>
        </div>

        {/* Items */}
        <div className="rounded-2xl border border-divider bg-surface p-6 space-y-4 shadow-sm">
          <h2 className="text-sm font-bold text-[color:var(--color-text)]">Productos comprados</h2>

          {/* Column headers */}
          <div className="grid grid-cols-12 gap-2 text-[10px] font-bold uppercase text-muted px-1 hidden sm:grid">
            <div className="col-span-4">Ingrediente / Insumo</div>
            <div className="col-span-2">Cantidad</div>
            <div className="col-span-2">Unidad</div>
            <div className="col-span-3">Precio unitario</div>
            <div className="col-span-1" />
          </div>

          <div className="space-y-2">
            {items.map((row, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center py-1 border-b border-divider/30 last:border-0">
                <div className="col-span-12 sm:col-span-4 space-y-1">
                  <div className="flex items-center gap-1">
                    <select
                      value={row.inventory_item_id}
                      onChange={e => handleSelectInventoryItem(i, e.target.value)}
                      className="flex-1 rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      aria-label="Ingrediente"
                    >
                      <option value="">-- Insumo --</option>
                      {inventoryItems.map(item => (
                        <option key={item.id} value={item.id}>{item.name} ({item.unit})</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowQuickItem({ active: true, rowIndex: i })}
                      className="h-9 px-2 rounded-xl border border-divider text-muted hover:text-primary hover:bg-primary/5 transition"
                    >
                      <Plus size={14} />
                    </button>
                  </div>

                  {(() => {
                    const sel = inventoryItems.find(item => String(item.id) === String(row.inventory_item_id));
                    if (sel && sel.purchase_unit && sel.conversion_factor && sel.conversion_factor > 1) {
                      return (
                        <div className="text-[10px] text-primary font-medium pl-1">
                          Equivale a: 1 {sel.purchase_unit} = {sel.conversion_factor} {sel.unit}
                        </div>
                      );
                    }
                    return null;
                  })()}


                  {showQuickItem.active && showQuickItem.rowIndex === i && (
                    <div className="p-2 rounded-lg border border-divider bg-surface shadow-sm space-y-2">
                      <input
                        type="text" autoFocus
                        placeholder="Nombre del insumo..."
                        value={quickItemName}
                        onChange={e => setQuickItemName(e.target.value)}
                        className="w-full rounded border border-divider px-2 py-1 text-xs"
                      />
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setShowQuickItem({ active: false, rowIndex: -1 })} className="text-[10px] text-muted">X</button>
                        <button
                          onClick={async () => {
                            if (!quickItemName) return;
                            try {
                              const res = await addItemMut.mutateAsync({
                                name: quickItemName,
                                unit: row.unit || 'kg'
                              });
                              // Forzar recarga inmediata del inventario
                              await queryClient.refetchQueries({ queryKey: ['inventory', 'items'] });

                              // ensure we store id as string to match InventoryItem.id
                              changeItem(i, 'inventory_item_id', String(res.id));
                              setShowQuickItem({ active: false, rowIndex: -1 });
                              setQuickItemName('');
                              toast.success('Insumo agregado y seleccionado');
                            } catch (e) {
                              toast.error('Error al crear insumo');
                            }
                          }}
                          className="text-[10px] font-bold text-primary"
                        >
                          Crear
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <input
                    type="number" min={0} step="any"
                    value={row.quantity}
                    onChange={e => changeItem(i, 'quantity', parseFloat(e.target.value) || 0)}
                    className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    aria-label="Cantidad"
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <select
                    value={row.unit}
                    onChange={e => changeItem(i, 'unit', e.target.value)}
                    className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    aria-label="Unidad"
                  >
                    {row.unit && !UNITS.includes(row.unit) && (
                      <option value={row.unit}>{row.unit}</option>
                    )}
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="col-span-3 sm:col-span-3">
                  {(() => {
                    const sel = inventoryItems.find(inv => String(inv.id) === String(row.inventory_item_id));
                    const refPrice = sel
                      ? Number(sel.purchase_price || ((sel.cost || 0) * (sel.conversion_factor || 1)))
                      : 0;
                    const price = Number(row.unit_price) || 0;
                    const diff = (refPrice > 0 && price > 0) ? price - refPrice : 0;
                    const pct = refPrice > 0 ? Number(((diff / refPrice) * 100).toFixed(1)) : 0;

                    return (
                      <>
                        <input
                          type="number" min={0} step="any"
                          value={row.unit_price || ''}
                          onChange={e => changeItem(i, 'unit_price', parseFloat(e.target.value) || 0)}
                          placeholder="Precio/unidad"
                          className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          aria-label="Precio unitario"
                        />
                        {refPrice > 0 && (
                          <div className="mt-1 flex items-center justify-between text-[10px] leading-tight">
                            <span className="text-muted">Ref: {fmt(refPrice)}</span>
                            {price > 0 && diff > 0.01 && (
                              <span className="text-red-600 font-bold">
                                🔺 +{pct}% (+{fmt(diff)})
                              </span>
                            )}
                            {price > 0 && diff < -0.01 && (
                              <span className="text-emerald-600 font-bold">
                                🟢 -{Math.abs(pct)}% (-{fmt(Math.abs(diff))})
                              </span>
                            )}
                            {price > 0 && Math.abs(diff) <= 0.01 && (
                              <span className="text-blue-600 font-semibold">
                                ✅ Mismo precio
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="col-span-1 flex justify-center">
                  <button onClick={() => removeItem(i)} className="flex h-8 w-8 items-center justify-center rounded-full text-red-400 hover:bg-red-50 transition" aria-label="Eliminar">
                    <Trash2 size={14} />
                  </button>
                </div>
                {/* Line total */}
                <div className="col-span-12 sm:col-start-9 sm:col-span-4 text-right text-xs text-muted pr-10">
                  {row.quantity > 0 && row.unit_price > 0 &&
                    <span className="font-semibold text-[color:var(--color-text)]">
                      = {fmt(row.quantity * row.unit_price)}
                    </span>
                  }
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={addItem}
            className="inline-flex items-center gap-2 rounded-full border border-dashed border-primary/50 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/5 transition"
          >
            <Plus size={14} /> Agregar ítem
          </button>
        </div>

        {/* Totals + Submit */}
        <div className="rounded-2xl border border-divider bg-surface p-6 shadow-sm">
          <div className="max-w-xs ml-auto space-y-2 text-sm">
            <div className="flex justify-between text-muted">
              <span>Subtotal</span><span className="font-semibold text-[color:var(--color-text)]]">{fmt(subtotal)}</span>
            </div>
            <div className="flex justify-between text-muted">
              <span>Impuestos</span><span>{fmt(tax)}</span>
            </div>
            <div className="flex justify-between border-t border-divider pt-2 font-bold text-base text-[color:var(--color-text)]">
              <span>Total</span><span>{fmt(total)}</span>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 mt-5">
            <button
              onClick={() => navigate('/purchases')}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-divider px-5 text-sm font-semibold hover:bg-surface-muted transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={createOrderMut.isPending || creatingItems}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-primary px-6 text-sm font-bold text-white shadow hover:opacity-90 disabled:opacity-60 transition"
            >
              {creatingItems
                ? <><Loader2 size={14} className="animate-spin" /> Creando insumos…</>
                : createOrderMut.isPending
                  ? <><Loader2 size={14} className="animate-spin" /> Guardando…</>
                  : <><Save size={14} /> Guardar factura</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewPurchasePage;
